/* eslint-disable no-unused-vars */
import { XhrError } from '../../../remote/request.js';
import { initPaymentConfig, saveOrderPayment, initProductCart, processPayment } from './payment-utils.js';

/**
 * Google Pay Implementation
 * Handles Google Pay button display, payment flow, and cart integration
 */

// Type definitions for Google Pay API
/**
 * @typedef {Object} GooglePaymentsClient
 * @property {function} isReadyToPay - Checks if Google Pay is available
 * @property {function} loadPaymentData - Initiates payment sheet
 * @property {function} createButton - Creates Google Pay button
 */

const cart_source = 'googlepay';
const dataForm = 'googlePayForm';

// Add caching for PaymentsClient instances
const paymentClientsCache = {};

/**
 * Extract readable error message from various error types
 */
const getErrorMessage = function(error) {

	if (error instanceof XhrError && error.responseJSON?.message) {
		return error.responseJSON.message;
	}

	if (error.message) {
		return error.message;
	}

	if (typeof error === 'string') {
		return error;
	}


	return mpro.langvar('error_unknown');
};

/**
 * Creates and caches Google Pay client instance
 * Configures environment and merchant information
 *
 * https://developers.google.com/pay/api/web/reference/request-objects#PaymentOptions
 */
const getGooglePaymentsClient = function(options) {
	// @ts-ignore
	if (!window.google?.payments?.api) {
		return null;
	}
	const key = JSON.stringify(options ?? {});
	if (paymentClientsCache[key]) {
		return paymentClientsCache[key];
	}
	// @ts-ignore
	paymentClientsCache[key] = new google.payments.api.PaymentsClient(options);
	return paymentClientsCache[key];
};

/**
 * Main handler for Google Pay button clicks and form submissions
 */
/**  @this {any} */
const googlePayHandler = async function(e) {

	// Prevent event bubbling to avoid double triggers
	e.preventDefault();
	// e.stopPropagation();

	// Important to use .call to keep same context for this
	const config = initPaymentConfig.call(this, e, {
		cart_source,
		dataForm,
		paymentMethod: 'google-pay'  // Important for getting correct dataset
	});

	if (!config) {
		return false;
	}

	// Validate payment request data
	if (!config.payOptions?.paymentRequest) {
		console.error('Google Pay payment request configuration is missing');
		return false;
	}

	const handlePaymentError = function(error) {
		// @ts-ignore
		if (error?.statusCode === 'CANCELED') {
			console.log(error);
			// canceled by user
			// Do nothing, just remove the button loader.
		}
		else {
			const message = getErrorMessage(error);
			mpro.toast('error', message);
		}

		mpro.removeButtonLoading(config.$submitButton);

		config.$submitButton.off('click').on('click', googlePayHandler);
	};

	const paymentsClient = getGooglePaymentsClient({ "environment": config.payOptions.environment });
	if (!paymentsClient) {
		handlePaymentError('Google Pay is not available');
		return false;
	}

	mpro.addButtonLoading(config.$submitButton);

	try {
		if (config.isProductPayment) {
			await initProductCart(config);
		}

		// Init payment sheet and handle payment data
		const paymentData = await paymentsClient.loadPaymentData(config.payOptions.paymentRequest);

		// Process based on payment type
		if (config.isProductPayment) {
			const postData = prepareProductPaymentData(paymentData);
			await processProductPayment(postData, config, handlePaymentError);
		} else {
			await processCheckoutPayment(paymentData, config, handlePaymentError);
		}
		return true;
	} catch (err) {
		handlePaymentError(err);
		return false;
	}
};

/**
 * Prepares cart data from Google Pay response
 * Extracts billing and shipping information from payment data
 */
const prepareProductPaymentData = function(paymentData) {
	const { paymentMethodData: { info } } = paymentData;
	const { billingAddress, shippingAddress } = info;

	const formData = new URLSearchParams();

	if (billingAddress) {
		formData.append('customer[billing_name]', billingAddress.name);
		formData.append('customer[billing_country_code]', billingAddress.countryCode);
		formData.append('customer[billing_state]', billingAddress.administrativeArea);
		formData.append('customer[billing_city]', billingAddress.locality);
		formData.append('customer[billing_address]', billingAddress.address1);
		formData.append('customer[billing_postal_code]', billingAddress.postalCode);
	}

	if (shippingAddress) {
		formData.append('customer[shipping_as_billing]', 'false');
		formData.append('customer[shipping_name]', shippingAddress.name);
		formData.append('customer[shipping_phone]', shippingAddress.phoneNumber);
		// ...rest of shipping address fields...
	}

	return formData;
};

/**
 * Handles product-specific payment processing
 * Manages cart creation and order processing for single products
 */
const processProductPayment = async function(formData, params, handleError) {
	try {
		const paymentData = await params.cart.processOrder(formData);
		if (paymentData.errors) {
			const errorMessage = Array.isArray(paymentData.errors)
				? paymentData.errors.map(v => mpro.langvar(v.message) || v.message || v).join('<br />')
				: mpro.langvar('error_pay_processing');
			handleError(errorMessage);
			return;
		}

		const result = await processPayment({
			paymentData,
			payOptions: params.payOptions,
			handleError
		});

		if (result.success) {
			// @ts-ignore
			mpro.setLocation(result.url);
		}
	} catch (error) {
		console.warn('Error processing payment:', error);
		handleError(error);
	}
};

/**
 * Handles cart checkout payment processing
 * Manages existing cart checkout and order processing
 */
const processCheckoutPayment = async function(paymentData, params, handleError) {
	try {
		const response = await saveOrderPayment(params.$paymentForm);
		const orderPaymentData = await response.json();

		if (orderPaymentData.errors) {
			const errorMessage = Array.isArray(orderPaymentData.errors)
				? orderPaymentData.errors.map(v => mpro.langvar(v.message) || v.message || v).join('<br />')
				: mpro.langvar('error_pay_processing');
			handleError(errorMessage);
			return;
		}

		const result = await processPayment({
			paymentData: paymentData,
			orderPaymentData: orderPaymentData,
			payOptions: params.payOptions,
			handleError
		});

		if (result.success) {
			// @ts-ignore
			mpro.setLocation(result.url);
		}
	} catch (error) {
		console.warn('Error processing payment:', error);
		handleError(error);
	}
};

/**
 * Controls visibility of Google Pay elements
 * Shows/hides buttons and form elements based on Google Pay availability
 */
const toggleGooglePayElementsVisibility = ($hiddenElements) => {
	if ($hiddenElements.length === 0) {
		return;
	}

	const paymentsClient = getGooglePaymentsClient();
	if (!paymentsClient) {
		return;
	}

	let options = null;
	// The element has configuration
	if ($hiddenElements[0].dataset.paymentRequestPayload) {
		options = JSON.parse($hiddenElements[0].dataset.paymentRequestPayload);
	}
	// Use a standard default set
	else {
		options = {
			apiVersion: 2,
			apiVersionMinor: 0,
			allowedPaymentMethods: [{
				type: 'CARD',
				parameters: {
					allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
					allowedCardNetworks: ['MASTERCARD', 'VISA']
				}
			}]
		};
	}

	paymentsClient.isReadyToPay(options)
		.then(response => {
			if (response.result) {
				$hiddenElements.each(function() {
					// @ts-ignore
					const $element = $(this);
					if ($element.is('input[type=radio]')) {
						$element.parents(':hidden').show();
					} else {
						$element.show();
					}
				});
			}
		})
		.catch(err => {
			console.warn('Google Pay availability check failed:', err);
		});
};

/**
 * Waits for Google Pay API to become available
 * Implements retry mechanism with timeout
 */
const waitForGooglePay = ($hiddenElements, waitTimeElapsed) => {

	const maxWaitTime = 10_000;
	const delay = 500;

	// There is nothing to show
	if ($hiddenElements.length === 0) {
		return;
	}

	// GooglePay JS api is available
	// @ts-ignore
	if (window.google) {
		toggleGooglePayElementsVisibility($hiddenElements);
	}
	// Wait for Google JS api to load
	else if (waitTimeElapsed < maxWaitTime) {
		setTimeout(waitForGooglePay, delay, $hiddenElements, waitTimeElapsed + delay);
	}
};


// Initialize Google Pay functionality
mpro.addInitStatic(($container) => {
	const waitTimeElapsed = 0;
	// Look for google pay elements that need to be displayed
	const $hiddenElements = $container.find('[data-payment-handler="google_pay"]');

	waitForGooglePay($hiddenElements, waitTimeElapsed);
});

// Setup event handlers and form interactions
mpro.addInitDelegate(($) => {
	const $B = mpro.get$B();

	// Use more specific selectors to avoid double binding
	$B.on('click.googlePay', '[data-google-pay]:not(form)', googlePayHandler);
	$B.on('submit.googlePay', 'form[data-google-pay]', googlePayHandler);

	/**
	 * @this {any}
	*/
	const toggleData = function() {

		const $this = $(this);
		const $paymentForm = $this.closest('form');
		const $submitButton = $paymentForm.find('button[type=submit]');

		if ($this.data('paymentHandler') === 'google_pay') {
			$paymentForm.attr('data-google-pay', '');
			$paymentForm.attr('data-google-pay-payment-request', $this.attr('data-payment-request-payload'));
			$paymentForm.attr('data-google-pay-endpoint', $this.attr('data-payment-endpoint'));
			$paymentForm.attr('data-google-pay-environment', $this.attr('data-payment-environment'));
			$submitButton.attr('data-google-pay-form', '');
		}
		else {
			$paymentForm.removeAttr('data-google-pay');
			$paymentForm.removeAttr('data-google-pay-payment-request');
			$paymentForm.removeAttr('data-google-pay-environment');
			$submitButton.removeAttr('data-google-pay-endpoint');
			$submitButton.removeAttr('data-google-pay-form');
		}
	};

	// data-section-id

	$B.on('change.googlePay', 'input[type=radio][name*="checkout"][name*="[payment_method][value]"]', function() {
		toggleData.call(this);
	});
	if ($('input[type=radio][name*="checkout"][name*="[payment_method][value]"]:checked').length > 0) {
		toggleData.call($('input[type=radio][name*="checkout"][name*="[payment_method][value]"]:checked')[0]);
	}
	mpro.get$D().on('mp-section-did-reload', (e) => {
		// @ts-ignore
		if (e.originalEvent.detail.$newSection) {
			// @ts-ignore
			const $input = e.originalEvent.detail.$newSection.find('input[type=radio][name*="checkout"][name*="[payment_method][value]"]:checked');
			if ($input.length > 0) {
				toggleData.call($input[0]);
			}
		}
	});
});
